import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSignUpMutation, useVerifyUserMutation } from "../../services/Api";
import { useDispatch } from "react-redux";
import { loggedIn } from "../../redux/AuthSlice";
import LoginModal from "./loginModal";
import OTPInput from "../OTPInput/OTPInput";

const SignupModal = (props) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [phone, setPhone] = useState("");
  const [cnic, setCnic] = useState("");
  const [error, setError] = useState("");
  const [otpCode, setOtpCode] = useState(null);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const [signUp, { isLoading: signUpLoading }] = useSignUpMutation();
  const [verifyUser, { isLoading: verifyLoading }] = useVerifyUserMutation();

  // Timer effect for OTP countdown
  useEffect(() => {
    let interval;
    if (otpCode && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [otpCode, timer]);

  const onSignUp = (data) => {
    const { first_name, last_name } = data || {};
    if (!first_name) {
      toast.error("First name is required!");
      return;
    }
    if (!last_name) {
      toast.error("Last name is required!");
      return;
    }
    if (!phone) {
      toast.error("Phone number is required!");
      return;
    }

    let params = {
      name: first_name + " " + last_name,
      phone_no: "+" + phone,
      cnic_number: cnic
    };

    if (phone?.length == 12) {
      setIsLoading(true);
      signUp({ data: params })
        .unwrap()
        .then((payload) => {
          const { code, status, message } = payload;
          if (status && code) {
            setOtpCode(code);
            setTimer(60);
            setCanResend(false);
            toast.success("OTP sent to your phone!");
          } else {
            toast.error(message || "Signup failed");
          }
        })
        .catch((error) => {
          console.error("Signup error:", error);
          toast.error("Signup failed. Please try again.");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      toast.error("Invalid phone number");
    }
  };

  const onVerifyLogin = () => {
    if (!otp) {
      toast.error("OTP is required!");
      return;
    }

    if (otp.length !== 4) {
      toast.error("Please enter complete 4-digit OTP");
      return;
    }

    let params = {
      register: 1,
      sms_code: otp,
      phone_no: "+" + phone
    };

    setIsLoading(true);
    verifyUser({ data: params })
      .unwrap()
      .then((payload) => {
        if (payload.status) {
          // User successfully signed up and logged in
          dispatch(loggedIn(payload));
          toast.success("Account created successfully! Welcome!");
          // Close all modals - user is now logged in
          props.handleCloseSignupModal();
        } else {
          toast.error(payload.message || "Invalid OTP. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Verification error:", error);
        const { code, status } = error.data || {};
        if (status && code) {
          setOtpCode(code);
          setTimer(60);
          setCanResend(false);
          toast.info("New OTP sent!");
        } else {
          toast.error("Verification failed. Please try again.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePhoneChange = (value) => {
    // Remove all non-numeric characters
    let cleanValue = value.replace(/\D/g, "");

    // Handle different input formats
    if (cleanValue.startsWith("920")) {
      // User typed 03... which became 9203...
      cleanValue = "92" + cleanValue.slice(3);
    } else if (cleanValue.startsWith("03")) {
      // User typed 03... (missing country code)
      cleanValue = "92" + cleanValue.slice(1);
    } else if (cleanValue.startsWith("3")) {
      // User typed 3... (missing 92)
      cleanValue = "92" + cleanValue;
    } else if (!cleanValue.startsWith("92")) {
      // Any other format, prepend 92
      cleanValue = "92" + cleanValue;
    }

    // Limit to 12 digits (92 + 10 digits)
    if (cleanValue.length > 12) {
      cleanValue = cleanValue.substring(0, 12);
    }

    setPhone(cleanValue);
  };

  const handleCnicChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) {
      setError("Please enter only numbers.");
      return;
    }
    setCnic(value);
    if (value.length < 13 && value.length > 0) {
      setError("CNIC must be exactly 13 digits.");
    } else if (value.length > 13) {
      setError("CNIC must be exactly 13 digits.");
    } else {
      setError("");
    }
  };

  const handleResendOTP = () => {
    setIsLoading(true);
    setTimeout(() => {
      setTimer(60);
      setCanResend(false);
      setIsLoading(false);
      toast.success("OTP resent successfully!");
    }, 1500);
  };

  const formatPhoneDisplay = (phoneNumber) => {
    if (!phoneNumber) return "";
    const formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{7})/, "+$1 $2 $3");
    return formatted;
  };

 const OtpInput = ({ value, onChange }) => {

  const handleChange = (index, e) => {
    const val = e.target.value;

    if (!/^\d?$/.test(val)) return;

    // always create 4 digit array
    const otpArray = value.padEnd(4, "").split("");

    otpArray[index] = val;

    const newOtp = otpArray.join("").trim();
    onChange(newOtp);

    // move focus
    if (val && index < 3) {
      const next = document.getElementById(`signup-otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleKeyDown = (index, e) => {

    if (e.key === "Backspace") {

      const otpArray = value.padEnd(4, "").split("");

      if (otpArray[index]) {
        otpArray[index] = "";
        onChange(otpArray.join("").trim());
      } 
      else if (index > 0) {
        const prev = document.getElementById(`signup-otp-${index - 1}`);
        prev?.focus();
      }

    }

  };

  return (
    <div style={styles.otpContainer}>
      {[0,1,2,3].map((index) => (
        <input
          key={index}
          id={`signup-otp-${index}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ""}
          onChange={(e)=>handleChange(index,e)}
          onKeyDown={(e)=>handleKeyDown(index,e)}
          style={styles.otpInput}
        />
      ))}
    </div>
  );
};

  // All styles defined as JavaScript objects
  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "16px"
    },
    modal: {
      backgroundColor: "white",
      borderRadius: "16px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      maxWidth: "400px",
      width: "100%",
      maxHeight: "90vh",
      overflowY: "auto",
      position: "relative"
    },
    container: {
      padding: "32px",
      position: "relative"
    },
    closeButton: {
      position: "absolute",
      top: "16px",
      right: "16px",
      background: "none",
      border: "none",
      fontSize: "24px",
      cursor: "pointer",
      color: "#6b7280",
      padding: "8px",
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      zIndex: 10
    },
    closeButtonHover: {
      backgroundColor: "#f3f4f6",
      color: "#374151"
    },
    header: {
      textAlign: "center",
      marginBottom: "32px"
    },
    headerIcon: {
      width: "80px",
      height: "80px",
      background: "linear-gradient(135deg, #10b981, #059669)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 16px",
      fontSize: "40px",
      color: "white"
    },
    otpHeaderIcon: {
      background: "linear-gradient(135deg, #3b82f6, #8b5cf6)"
    },
    title: {
      fontSize: "28px",
      fontWeight: "bold",
      color: "#111827",
      marginBottom: "8px",
      margin: "0",
      fontFamily: "'Inter', sans-serif"
    },
    subtitle: {
      color: "#6b7280",
      fontSize: "16px",
      margin: "0",
      lineHeight: "1.5"
    },
    formGroup: {
      marginBottom: "24px"
    },
    row: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
      marginBottom: "24px"
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "8px"
    },
    inputGroup: {
      position: "relative"
    },
    icon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
      fontSize: "20px",
      zIndex: 1
    },
    input: {
      width: "100%",
      paddingLeft: "48px",
      paddingRight: "16px",
      paddingTop: "12px",
      paddingBottom: "12px",
      border: "1px solid #d1d5db",
      borderRadius: "12px",
      fontSize: "16px",
      outline: "none",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
      fontFamily: "'Inter', sans-serif",
      backgroundColor: "white"
    },
    inputFocus: {
      borderColor: "#10b981",
      boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)"
    },
    button: {
      width: "100%",
      background: "linear-gradient(135deg, #10b981, #059669)",
      color: "white",
      fontWeight: "600",
      padding: "16px 24px",
      border: "none",
      borderRadius: "12px",
      fontSize: "16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontFamily: "'Inter', sans-serif"
    },
    otpButton: {
      background: "linear-gradient(135deg, #3b82f6, #8b5cf6)"
    },
    buttonHover: {
      transform: "scale(1.02)",
      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)"
    },
    otpButtonHover: {
      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)"
    },
    buttonDisabled: {
      opacity: "0.7",
      cursor: "not-allowed"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid white",
      borderTop: "2px solid transparent",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    },
    error: {
      color: "#ef4444",
      fontSize: "12px",
      marginTop: "4px",
      fontWeight: "500"
    },
    linkButton: {
      background: "none",
      border: "none",
      color: "#10b981",
      fontWeight: "500",
      cursor: "pointer",
      textDecoration: "underline",
      padding: "4px 8px",
      fontSize: "14px",
      transition: "color 0.2s ease",
      fontFamily: "'Inter', sans-serif"
    },
    linkButtonHover: {
      color: "#059669"
    },
    linkButtonDisabled: {
      color: "#9ca3af",
      cursor: "not-allowed",
      textDecoration: "none"
    },
    backButton: {
      width: "100%",
      marginTop: "16px",
      color: "#6b7280",
      fontWeight: "500",
      padding: "8px",
      background: "none",
      border: "none",
      cursor: "pointer",
      transition: "color 0.2s ease",
      fontFamily: "'Inter', sans-serif"
    },
    backButtonHover: {
      color: "#374151"
    },
    phoneDisplay: {
      fontWeight: "600",
      color: "#10b981",
      marginBottom: "16px",
      fontSize: "18px"
    },
    timerText: {
      color: "#6b7280",
      fontSize: "14px",
      marginBottom: "8px"
    },
    otpContainer: {
      display: "flex",
      justifyContent: "center",
      gap: "12px",
      marginBottom: "24px"
    },
    otpInput: {
      width: "56px",
      height: "56px",
      textAlign: "center",
      fontSize: "20px",
      fontWeight: "600",
      backgroundColor: "white",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      outline: "none",
      transition: "all 0.2s ease",
      fontFamily: "'Inter', sans-serif",
      cursor: "text",
      userSelect: "auto",
      pointerEvents: "auto"
    },
    otpInputFocus: {
      borderColor: "#10b981",
      boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)"
    }
  };

  const spinKeyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;

  const [closeButtonHover, setCloseButtonHover] = useState(false);
  const [backButtonHover, setBackButtonHover] = useState(false);
  const [linkButtonHover, setLinkButtonHover] = useState(false);

  return (
    <>
      <style>{spinKeyframes}</style>
      {!showLoginModal && (
        <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.container}>
            {/* Close Button */}
            <button
              onClick={() => {
                // Close signup modal
                setShowLoginModal(false);
                // Also close parent login modal
                props.handleCloseSignupModal();
              }}
              style={{
                ...styles.closeButton,
                ...(closeButtonHover ? styles.closeButtonHover : {})
              }}
              onMouseEnter={() => setCloseButtonHover(true)}
              onMouseLeave={() => setCloseButtonHover(false)}
            >
              ×
            </button>

            {!otpCode ? (
              <>
                {/* Header */}
                <div style={styles.header}>
                  <div style={styles.headerIcon}>👤</div>
                  <h1 style={styles.title}>Create Account</h1>
                  <p style={styles.subtitle}>Join us with your phone number</p>
                </div>

                {/* Name Fields */}
                <div style={styles.row}>
                  <div>
                    <label style={styles.label}>First Name</label>
                    <div style={styles.inputGroup}>
                      <span style={styles.icon}>👤</span>
                      <input
                        type="text"
                        placeholder="AL"
                        {...register("first_name", { required: true })}
                        style={styles.input}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#10b981";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(16, 185, 129, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                    </div>
                    {errors.first_name && (
                      <p style={styles.error}>First name is required</p>
                    )}
                  </div>

                  <div>
                    <label style={styles.label}>Last Name</label>
                    <div style={styles.inputGroup}>
                      <span style={styles.icon}>👤</span>
                      <input
                        type="text"
                        placeholder="Ahmad"
                        {...register("last_name", { required: true })}
                        style={styles.input}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#10b981";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(16, 185, 129, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                    </div>
                    {errors.last_name && (
                      <p style={styles.error}>Last name is required</p>
                    )}
                  </div>
                </div>

                {/* Phone Number */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone Number</label>
                  <div style={styles.inputGroup}>
                    <span style={styles.icon}>📞</span>
                    <input
                      type="tel"
                      placeholder="+92 300 1234567"
                      value={formatPhoneDisplay(phone)}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      style={styles.input}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#10b981";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(16, 185, 129, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>

                {/* CNIC */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>CNIC Number</label>
                  <div style={styles.inputGroup}>
                    <span style={styles.icon}>💳</span>
                    <input
                      type="text"
                      placeholder="1234567890123"
                      value={cnic}
                      onChange={handleCnicChange}
                      maxLength={13}
                      style={styles.input}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#10b981";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(16, 185, 129, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  {error && <p style={styles.error}>{error}</p>}
                </div>

                {/* Submit Button */}
                <button
                  type="button"
                  onClick={handleSubmit(onSignUp)}
                  disabled={isLoading}
                  style={{
                    ...styles.button,
                    ...(isLoading ? styles.buttonDisabled : {})
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) {
                      e.target.style.transform = "scale(1.02)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(16, 185, 129, 0.2)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  {isLoading ? (
                    <>
                      <div style={styles.spinner}></div>
                      <span>Sending OTP...</span>
                    </>
                  ) : (
                    <>
                      <span>🔐</span>
                      <span>REQUEST OTP</span>
                    </>
                  )}
                </button>

                {/* Login Link */}
                <div style={{ textAlign: "center", marginTop: "16px" }}>
                  <p style={styles.subtitle}>
                    Already have an account?{" "}
                    <button
                      onClick={() => setShowLoginModal(true)}
                      style={{
                        ...styles.linkButton,
                        textDecoration: "none",
                        fontWeight: "600"
                      }}
                    >
                      Login
                    </button>
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* OTP Verification */}
                <div style={styles.header}>
                  <div
                    style={{ ...styles.headerIcon, ...styles.otpHeaderIcon }}
                  >
                    🔐
                  </div>
                  <h1 style={styles.title}>Verify Your Phone</h1>
                  <p style={styles.subtitle}>Enter the 4-digit code sent to</p>
                  <p style={styles.phoneDisplay}>{formatPhoneDisplay(phone)}</p>
                </div>

                {/* OTP Input */}
                <OTPInput value={otp} onChange={setOtp} disabled={isLoading} />

                {/* Verify Button */}
                <button
                  onClick={onVerifyLogin}
                  disabled={isLoading || otp.length !== 4}
                  style={{
                    ...styles.button,
                    ...styles.otpButton,
                    ...(isLoading || otp.length !== 4
                      ? styles.buttonDisabled
                      : {}),
                    marginBottom: "16px"
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading && otp.length === 4) {
                      e.target.style.transform = "scale(1.02)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(59, 130, 246, 0.2)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  {isLoading ? (
                    <>
                      <div style={styles.spinner}></div>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>✓</span>
                      <span>VERIFY OTP</span>
                    </>
                  )}
                </button>

                {/* Timer and Resend OTP */}
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  {!canResend ? (
                    <p style={styles.timerText}>
                      Resend OTP in {timer} seconds
                    </p>
                  ) : (
                    <>
                      <p style={{ ...styles.subtitle, marginBottom: "8px" }}>
                        Didn't receive the code?
                      </p>
                      <button
                        onClick={handleResendOTP}
                        disabled={isLoading}
                        style={{
                          ...styles.linkButton,
                          ...(isLoading ? styles.linkButtonDisabled : {}),
                          ...(linkButtonHover ? styles.linkButtonHover : {})
                        }}
                        onMouseEnter={() => setLinkButtonHover(true)}
                        onMouseLeave={() => setLinkButtonHover(false)}
                      >
                        {isLoading ? "Resending..." : "Resend OTP"}
                      </button>
                    </>
                  )}
                </div>

                {/* Back Button */}
                <button
                  onClick={() => {
                    setOtpCode(null);
                    setTimer(60);
                    setCanResend(false);
                    setOtp("");
                  }}
                  style={{
                    ...styles.backButton,
                    ...(backButtonHover ? styles.backButtonHover : {})
                  }}
                  onMouseEnter={() => setBackButtonHover(true)}
                  onMouseLeave={() => setBackButtonHover(false)}
                >
                  ← Back to Signup
                </button>
              </>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal 
          handleCloseSignupModal={() => {
            setShowLoginModal(false);
            props.handleCloseSignupModal();
          }} 
        />
      )}
    </>
  );
};

export default SignupModal;
